/* eslint-disable react/prop-types */

import type { Carer } from '@lmc/ts-common'
import { canManagePayAndInvoicing } from 'common/permissions'
import type { TabBarItem } from 'components/generic/TabBar/TabBar'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import type { FC } from 'react'
import { useSelector } from 'react-redux'
import { type Location, Navigate, Outlet } from 'react-router-dom'
import { TabBar } from '../../../components/generic'
import { withRouter } from '../../../withRouter'
import { RotaPaywallScreen } from './RotaPaywallScreen'

type NavItem = {
  url: string
  label: string
}

type RouterProp = {
  location: Location
  navigate: (to: string) => void
  params: Record<string, string | undefined>
}

const RotaRouteScreen: FC<{ location: Location; router: RouterProp }> = ({
  location,
  router
}) => {
  const { data: flags, isLoading: areFlagsLoading } = useFeatureFlags({
    flagNames: ['published_rota', 'payrates']
  })
  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )

  const { hasRota } = useHomeType()

  const flagsPublishedRotaEnabled = flags?.published_rota?.enabled
  const payratesEnabled = flags?.payrates?.enabled
  const canAccessReconciliation = canManagePayAndInvoicing(userRole)
  const isReconciliationPath = router.location.pathname.startsWith(
    '/rota/reconciliation'
  )
  // Both inputs to the redirect below read as "no access" before they have
  // loaded: `useFeatureFlags` seeds every flag to `{ enabled: false }` while the
  // request is in flight, and `user.role` is undefined until /v5/init hydrates
  // the store. Redirecting on those first-paint values sent every deep link and
  // bookmark to /rota/builder even for homes that do have pay rates — the whole
  // Pay Rates E2E suite failed on navigate() for a fortnight (ROST-3505).
  const isAccessStateResolving = areFlagsLoading || userRole == null

  let items: NavItem[] = []

  if (!hasRota) {
    items = []
  } else {
    const baseTabs = flagsPublishedRotaEnabled
      ? NAV_TABS_WITH_PUBLISHED
      : NAV_TABS_WITH_MONITORING

    items =
      payratesEnabled && canAccessReconciliation
        ? [...baseTabs, { url: 'reconciliation', label: 'Reconciliation' }]
        : baseTabs
  }

  return (
    <div>
      {!isAccessStateResolving &&
        (!payratesEnabled || !canAccessReconciliation) &&
        isReconciliationPath && <Navigate to="/rota/builder" replace />}
      <TabBar
        // @ts-ignore
        items={items}
        resourceUrl="rota"
        location={location}
      />
      <Outlet />
      {hasRota ? (
        router.location.pathname === '/rota' && <Navigate to="builder" />
      ) : (
        <RotaPaywallScreen />
      )}
    </div>
  )
}

export default withRouter(RotaRouteScreen)

const getSearchWithoutTabKey = () => {
  const searchParams = new URLSearchParams(window.location.search)
  searchParams.delete('tabKey')
  const nextSearch = searchParams.toString()

  return nextSearch ? `?${nextSearch}` : ''
}

const NAV_TABS_WITH_MONITORING = [
  {
    url: 'monitoring',
    label: 'Monitoring'
  },
  {
    url: 'builder',
    label: 'Rota Builder'
  },
  {
    url: 'patterns',
    label: 'Manage Patterns'
  }
]

const NAV_TABS_WITH_PUBLISHED: TabBarItem[] = [
  {
    url: 'published',
    label: 'Published',
    search: getSearchWithoutTabKey
  },
  {
    url: 'monitoring',
    label: 'Monitoring'
  },
  {
    url: 'builder',
    label: 'Builder',
    search: getSearchWithoutTabKey
  },
  {
    url: 'patterns',
    label: 'Manage Patterns'
  }
]
